import { useEffect, useRef } from "react";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../utils/firebase";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import fetching from "../helpers/fetching/fetching";
import { LOCAL_STORAGE_USER_TOKEN_KEY } from "../utils/app-info";
import NotificationToast from "../components/alerts/NotificationToast";

import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../redux/features/user/userSlice";
import { useQueryClient } from "@tanstack/react-query";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_KEY_PAIR;

const useFirebaseNotifications = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userData = useSelector((state: any) => state.user.userData);
  const initialized = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_USER_TOKEN_KEY);
    if (!token) return;

    const setupNotifications = async () => {
      try {
        // console.log("📡 [FCM] Registration started...");

        if (!("serviceWorker" in navigator)) {
          // console.error("❌ [FCM] SW not supported");
          return;
        }

        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );
        // console.log("✅ [FCM] SW Registered:", registration.scope);

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          // console.warn("⚠️ [FCM] Permission denied");
          return;
        }

        const fcmToken = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (fcmToken) {
          // console.log("🎟️ [FCM] Token retrieved:", fcmToken);
          if (!userData.fcm_token || userData.fcm_token !== fcmToken) {
            const response = await fetching(
              "/me",
              token,
              "PUT",
              JSON.stringify({ fcm_token: fcmToken }),
              "application/json",
            );
            if (response.ok) {
              // console.log("✅ [FCM] Token synced with backend");
              dispatch(setUserData({ ...userData, fcm_token: fcmToken }));
            }
          }
        }
      } catch (error) {
        console.error("❌ [FCM] Setup error:", error);
      }
    };

    if (!initialized.current) {
      initialized.current = true;
      setupNotifications();
    }
  }, [userData, dispatch]);

  useEffect(() => {
    // console.log("👂 [FCM] Subscribing to foreground messages...");

    const unsubscribe = onMessage(messaging, (payload) => {
      //console.log("🔥 [FCM] Foreground message caught!", payload);

      const { title, body } = payload.notification || {};
      const { type } = payload.data || {};

      // Invalidate relevant queries
      if (type === "business_offer") {
        queryClient.invalidateQueries({ queryKey: ["business-offers"] });
      } else if (
        type === "job_posting" ||
        type === "business_application_accepted"
      ) {
        queryClient.invalidateQueries({ queryKey: ["job-postings"] });
      } else if (type === "wallet") {
        queryClient.invalidateQueries({ queryKey: ["wallet-transactions"] });
      }

      const routeMap: Record<string, string> = {
        business_application_accepted: "/jobs",
        business_offer: "/offers",
        job_posting: "/jobs",
        wallet: "/transactions",
      };

      const route = type ? routeMap[type] : undefined;

      enqueueSnackbar(title || "New Notification", {
        anchorOrigin: { vertical: "top", horizontal: "center" },
        content: (key) => (
          <NotificationToast
            id={key}
            title={title || "New Notification"}
            body={body || ""}
            route={route}
          />
        ),
      });
    });

    return () => {
      unsubscribe();
    };
  }, [enqueueSnackbar, queryClient]);
};

export default useFirebaseNotifications;
